import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';

interface PerfectLevelContextType {
  isPerfectLesson: boolean;
  markLessonImperfect: () => void;
  resetLevelPerfectStatus: () => void;
  levelPerfectStatus: Record<number, boolean>;
  markLevelImperfect: (levelIndex: number) => void;
  getLevelPerfectStatus: (levelIndex: number) => boolean;
  allLevelsPerfect: () => boolean;
}

const PerfectLevelContext = createContext<PerfectLevelContextType | undefined>(undefined);

export const PerfectLevelProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [isPerfectLesson, setIsPerfectLesson] = useState(true);
  const [levelPerfectStatus, setLevelPerfectStatus] = useState<Record<number, boolean>>({});
  
  const markLessonImperfect = useCallback(() => {
    console.log('🏆 [PerfectLevelContext] Marking lesson as imperfect');
    setIsPerfectLesson(false);
  }, []);
  
  const resetLevelPerfectStatus = useCallback(() => {
    console.log('🏆 [PerfectLevelContext] Resetting perfect status for new lesson');
    setIsPerfectLesson(true);
    setLevelPerfectStatus({});
  }, []);
  
  const markLevelImperfect = useCallback((levelIndex: number) => {
    console.log(`🏆 [PerfectLevelContext] Marking level ${levelIndex} as imperfect`);
    setLevelPerfectStatus(prev => ({
      ...prev,
      [levelIndex]: false
    }));
    // When any level is imperfect, the lesson is imperfect
    markLessonImperfect();
  }, [markLessonImperfect]);
  
  const getLevelPerfectStatus = useCallback((levelIndex: number) => {
    // If the level has been explicitly marked as imperfect, return false
    // Otherwise, assume it's perfect (default to true)
    return levelPerfectStatus[levelIndex] !== false;
  }, [levelPerfectStatus]);
  
  const allLevelsPerfect = useCallback(() => {
    // Check if any level has been explicitly marked as imperfect
    const imperfectLevels = Object.values(levelPerfectStatus).filter(status => status === false);
    console.log(`🏆 [PerfectLevelContext] Checking all levels perfect: ${imperfectLevels.length === 0}`);
    return imperfectLevels.length === 0;
  }, [levelPerfectStatus]);
  
  return (
    <PerfectLevelContext.Provider 
      value={{ 
        isPerfectLesson, 
        markLessonImperfect, 
        resetLevelPerfectStatus, 
        levelPerfectStatus,
        markLevelImperfect,
        getLevelPerfectStatus,
        allLevelsPerfect
      }}
    >
      {children}
    </PerfectLevelContext.Provider>
  );
};

export const usePerfectLevel = () => {
  const context = useContext(PerfectLevelContext);
  if (!context) {
    throw new Error('usePerfectLevel must be used within a PerfectLevelProvider');
  }
  return context;
};
